import { useMemo, useState } from 'react';
import { Icon } from '../../components/ui/Icon.js';
import { Switch } from '../../components/ui/Form.js';
import { Flag } from '../../components/ui/Flag.js';
import { CONTINENTS, dialCode, type Continent, type Country } from './dialPolicies.js';

interface Place {
  iso2: string; name: string; prefixes: string[]; high_risk: boolean; continent: Continent;
  /** Countries dialled with exactly the same code, and so allowed or refused together. */
  shares: string[];
}

/**
 * International dialling: which countries may be called.
 *
 * Grouped by continent, because that is where people look for a country, with
 * the flag and the code they would dial. The account's own country is left out
 * -- a national call is never international -- and satellite and international
 * network ranges are a group of their own, last.
 *
 * Countries that share a code are switched together. The switch sees digits,
 * and a +1 number could be American or Canadian, so allowing one allows both; a
 * picker that let the two differ would describe something the switch cannot do.
 */
export function CountryPicker({ countries, homeCountry, value, onChange, disabled = false }: {
  countries: Country[];
  homeCountry: string;
  value: Set<string>;
  onChange: (next: Set<string>) => void;
  disabled?: boolean;
}) {
  const [search, setSearch] = useState('');
  // Groups already holding a choice start open, so what is allowed is visible.
  const [open, setOpen] = useState<Set<Continent>>(
    () => new Set(countries.filter((c) => value.has(c.iso2)).map((c) => c.continent)));

  const { places, byIso } = useMemo(() => {
    const map = new Map<string, Place>();
    const owners = new Map<string, Set<string>>();
    for (const c of countries) {
      owners.set(c.dial_prefix, (owners.get(c.dial_prefix) ?? new Set<string>()).add(c.iso2));
      const p = map.get(c.iso2);
      if (p) {
        p.prefixes.push(c.dial_prefix);
        p.high_risk ||= c.is_high_risk;
      } else {
        map.set(c.iso2, {
          iso2: c.iso2, name: c.name, prefixes: [c.dial_prefix],
          high_risk: c.is_high_risk, continent: c.continent, shares: [],
        });
      }
    }
    for (const p of map.values()) {
      p.prefixes.sort((a, b) => a.length - b.length || a.localeCompare(b));
      p.shares = [...new Set(p.prefixes.flatMap((x) => [...(owners.get(x) ?? [])]))]
        .filter((i) => i !== p.iso2);
    }
    /**
     * Anything dialled under the home country's own code is a national call to
     * the switch, whatever its flag -- so it is not offered as a country abroad.
     */
    const home = map.get(homeCountry)?.prefixes ?? [];
    const list = [...map.values()]
      .filter((p) => p.iso2 !== homeCountry
        && !p.prefixes.every((x) => home.some((h) => x.startsWith(h))))
      .sort((a, b) => a.name.localeCompare(b.name));
    return { places: list, byIso: map };
  }, [countries, homeCountry]);

  const q = search.trim().toLowerCase();
  const digits = q.replace(/\D/g, '');
  const matches = (p: Place) => !q
    || p.name.toLowerCase().includes(q)
    || p.iso2.toLowerCase() === q
    || (digits.length > 0 && p.prefixes.some((x) => x.startsWith(digits)));

  const set = (isos: string[], on: boolean) => {
    const next = new Set(value);
    for (const iso of isos) {
      for (const i of [iso, ...(byIso.get(iso)?.shares ?? [])]) {
        if (on) next.add(i); else next.delete(i);
      }
    }
    onChange(next);
  };

  const homeContinent = byIso.get(homeCountry)?.continent;
  const groups = CONTINENTS
    .map(([key, label]) => {
      const all = places.filter((p) => p.continent === key);
      return { key, label, all, shown: all.filter(matches), on: all.filter((p) => value.has(p.iso2)).length };
    })
    .filter((g) => g.all.length > 0)
    // The account's own continent first: an office in Johannesburg looks under Africa.
    .sort((a, b) => Number(b.key === homeContinent) - Number(a.key === homeContinent));

  const total = places.filter((p) => value.has(p.iso2)).length;
  const nameOf = (iso: string) => byIso.get(iso)?.name ?? iso;

  return (
    <div className="arc-stack" style={{ gap: 8 }}>
      <div className="arc-row" style={{ gap: 8 }}>
        <div className="arc-search" style={{ maxWidth: 320, flex: 1 }}>
          <Icon name="search" size={14} />
          <input className="arc-input" placeholder="Search country or dial code"
            aria-label="Search countries"
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="arc-spacer" />
        <span className="arc-muted" style={{ fontSize: 12 }}>
          {total} of {places.length} allowed
        </span>
        {total > 0 && !disabled && (
          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
            onClick={() => onChange(new Set())}>Clear all</button>
        )}
      </div>

      <div>
        {groups.map((g) => {
          if (q && g.shown.length === 0) return null;
          const expanded = !!q || open.has(g.key);
          const allOn = g.on === g.all.length;
          return (
            <div key={g.key} className="arc-continent">
              <div className="arc-continent-head">
                <button type="button" className="arc-continent-toggle" aria-expanded={expanded}
                  disabled={!!q}
                  onClick={() => setOpen((o) => {
                    const n = new Set(o);
                    if (n.has(g.key)) n.delete(g.key); else n.add(g.key);
                    return n;
                  })}>
                  <Icon name="chevronDown" size={13} />
                  <span>{g.label}</span>
                  <span className="arc-continent-count">
                    {g.on > 0 ? `${g.on} of ${g.all.length} allowed` : g.all.length}
                  </span>
                </button>
                {!disabled && (
                  <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                    onClick={() => set(g.all.map((p) => p.iso2), !allOn)}>
                    {allOn ? 'Clear' : 'Allow all'}
                  </button>
                )}
              </div>
              {expanded && (
                <div className="arc-continent-body">
                  {(q ? g.shown : g.all).map((p) => (
                    <label key={p.iso2} className="arc-country" data-on={value.has(p.iso2)}>
                      <Flag iso2={p.iso2} />
                      <span className="arc-truncate" style={{ flex: 1 }}>
                        {p.name}
                        {p.shares.length > 0 && (
                          <span className="arc-subtle"> · with {p.shares.map(nameOf).join(', ')}</span>
                        )}
                      </span>
                      <span className="arc-country-code">{p.prefixes.map(dialCode).join(', ')}</span>
                      {/* Flagged rather than refused outright: see "Allow high-risk
                          destinations" in the policy editor. */}
                      {p.high_risk && (
                        <span title="High-risk destination, commonly abused for toll fraud">
                          <Icon name="warning" size={12} />
                        </span>
                      )}
                      <Switch checked={value.has(p.iso2)} label={p.name} disabled={disabled}
                        onChange={(on) => set([p.iso2], on)} />
                    </label>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {q && groups.every((g) => g.shown.length === 0) && (
        <p className="arc-muted" style={{ fontSize: 12.5, padding: '6px 2px', margin: 0 }}>
          No country matches “{search}”.
        </p>
      )}
    </div>
  );
}
